import React, { useEffect, useState } from "react";
import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import StickyWhatsApp from "@/components/StickyWhatsApp";
import LanguageSwitcher, { LanguageSegment } from "@/components/LanguageSwitcher";
import CountrySwitcher, { isRivieraPath } from "@/components/CountrySwitcher";
import PublicFooter from "@/components/layout/PublicFooter";
import CookieConsent from "@/components/CookieConsent";
import { Sheet, SheetContent, SheetTitle, SheetClose } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth";
import { RIVIERA_CITIES } from "@/lib/riviera";

const linkCls = ({ isActive }) =>
  `text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "text-[#1B3A6B]" : "text-slate-600 hover:text-[#1B3A6B]"}`;
const BTN_GREEN = "inline-flex items-center justify-center rounded-full font-semibold whitespace-nowrap text-white bg-[#5BA83F] hover:bg-[#4C9133] active:scale-[0.98] transition-[background-color,transform] h-9 px-4 text-sm";
const BTN_OUTLINE = "inline-flex items-center justify-center rounded-full font-semibold whitespace-nowrap text-[#1B3A6B] border border-[#1B3A6B]/25 hover:border-[#1B3A6B] hover:bg-[#1B3A6B]/5 transition-colors h-9 px-4 text-sm";
const BTN_AZURE = "inline-flex items-center justify-center rounded-full font-semibold whitespace-nowrap text-white bg-[#0EA5E9] hover:bg-[#0284C7] active:scale-[0.98] transition-[background-color,transform] h-9 px-4 text-sm";

export default function PublicLayout() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { pathname } = useLocation();
  const riviera = isRivieraPath(pathname);
  useEffect(() => { setOpen(false); }, [pathname]);

  const NAV = [
    { to: "/about", label: t("nav.about"), testid: "nav-about" },
    { to: riviera ? "/fr#services" : "/services", label: t("nav.services"), testid: "nav-services" },
    ...(riviera ? [] : [{ to: "/service-areas", label: t("nav_v3.locations"), testid: "nav-locations" }]),
    { to: "/contact", label: t("nav_v3.contact"), testid: "nav-contact" },
  ];
  const member = !user || user.role === "customer"
    ? { to: user ? "/my-account" : "/login", label: t("nav_v3.manage_booking") }
    : user.role === "provider" ? { to: "/portal", label: t("nav.portal") }
    : { to: "/admin", label: t("nav.admin") };
  const book = riviera
    ? { to: "/fr/book", cls: BTN_AZURE, label: t("nav.book_now") }
    : { to: "/book", cls: BTN_GREEN, label: t("nav.book_now") };

  return (
    <div className="tl-public min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-200/60" data-testid="public-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-8 h-16 xl:h-[68px] flex items-center justify-between gap-3">
          <Link to={riviera ? "/fr" : "/"} className="flex items-center shrink-0 min-w-0" data-testid="nav-logo-link"><Logo /></Link>

          <nav className="hidden xl:flex items-center gap-6">
            {NAV.slice(0, 2).map((n) => <HeaderLink key={n.testid} n={n} />)}
            {riviera ? <CitiesMenu t={t} /> : <HeaderLink n={NAV[2]} />}
            <HeaderLink n={NAV[NAV.length - 1]} />
            <NavLink to={member.to} className={linkCls} data-testid="nav-member-login">{member.label}</NavLink>
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden xl:flex items-center gap-2">
              <CountrySwitcher compact />
              <LanguageSwitcher size={24} />
            </div>
            <Link to={book.to} className={`${book.cls} hidden sm:inline-flex`} data-testid="book-now-header-button">{book.label}</Link>
            <button className="xl:hidden p-2 -mr-2 rounded-md text-slate-700" onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle" aria-label="Toggle menu" aria-expanded={open}>
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </header>
      <MobileMenu t={t} open={open} setOpen={setOpen} riviera={riviera} nav={NAV} member={member} book={book} />

      <main className="flex-1"><Outlet /></main>
      <PublicFooter />
      <StickyWhatsApp />
      <CookieConsent />
    </div>
  );
}

function HeaderLink({ n }) {
  const hash = n.to.includes("#");
  return <NavLink to={n.to} className={hash ? linkCls({ isActive: false }) : linkCls} data-testid={n.testid}>{n.label}</NavLink>;
}

function CitiesMenu({ t }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-[#1B3A6B] outline-none" data-testid="nav-riviera-cities">
        {t("nav_v3.locations")} <ChevronDown className="w-3.5 h-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        {RIVIERA_CITIES.map((c) => (
          <DropdownMenuItem key={c.slug} asChild>
            <Link to={`/fr/${c.slug}`} className="cursor-pointer flex justify-between" data-testid={`nav-city-${c.slug}`}>
              {c.name}
              {c.inquiryOnly && <span className="text-[10px] font-bold text-amber-700">{t("riviera.inquiry_only")}</span>}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileMenu({ t, open, setOpen, riviera, nav, member, book }) {
  const close = () => setOpen(false);
  const item = "flex items-center min-h-[44px] text-base font-medium text-slate-700 hover:text-[#1B3A6B] border-b border-slate-100";
  const lbl = "text-[11px] uppercase tracking-[0.18em] font-bold text-slate-400";
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" aria-describedby={undefined}
        className="xl:hidden p-0 gap-0 flex flex-col w-[88vw] max-w-[400px] sm:max-w-[400px] h-[100dvh] bg-white [&>button:first-child]:hidden"
        style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="mobile-menu">
        <div className="flex items-center justify-between h-16 px-5 border-b border-slate-200 shrink-0">
          <SheetTitle asChild><Link to={riviera ? "/fr" : "/"} onClick={close}><Logo /></Link></SheetTitle>
          <SheetClose className="p-2 -mr-2 rounded-md text-slate-700 hover:bg-slate-100" aria-label="Close menu" data-testid="mobile-menu-close"><X /></SheetClose>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4" data-testid="mobile-menu-scroll">
          <div className="space-y-3 rounded-xl bg-slate-50 p-3" data-testid="mobile-menu-locale">
            <div className="flex flex-wrap items-center justify-between gap-2"><p className={lbl}>{t("footer_v3.country_label")}</p><CountrySwitcher testid="country-switcher-mobile" /></div>
            <div className="flex flex-wrap items-center justify-between gap-2"><p className={lbl}>{t("footer_v3.language_label")}</p><LanguageSegment testid="language-segment-mobile" /></div>
          </div>
          <nav className="mt-3 flex flex-col">
            {nav.map((n) => <NavLink key={n.testid} to={n.to} onClick={close} className={item} data-testid={`mobile-${n.testid}`}>{n.label}</NavLink>)}
            {riviera && (
              <div className="py-3 border-b border-slate-100">
                <p className={lbl}>{t("nav_v3.locations")}</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {RIVIERA_CITIES.map((c) => (
                    <Link key={c.slug} to={`/fr/${c.slug}`} onClick={close} className={`${c.inquiryOnly ? "col-span-2" : ""} flex items-center justify-between gap-1 min-h-[40px] px-3 rounded-lg ring-1 ring-slate-200 text-sm text-slate-700 hover:ring-[#0EA5E9] hover:text-[#0EA5E9]`} data-testid={`mobile-city-${c.slug}`}>
                      <span className="truncate">{c.name}</span>
                      {c.inquiryOnly && <span className="text-[9px] font-bold uppercase text-amber-700 shrink-0">{t("riviera.inquiry_only")}</span>}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            <Link to={member.to} onClick={close} className={item} data-testid="mobile-member-login">{member.label}</Link>
          </nav>
        </div>
        <div className="shrink-0 grid grid-cols-1 min-[420px]:grid-cols-2 gap-2 px-5 py-4 border-t border-slate-200 bg-white">
          <Link to={riviera ? "/fr/inquiry" : "/quote"} onClick={close} className={`${BTN_OUTLINE} !h-11`} data-testid="mobile-quote-btn">{t("home_v3.cta_quote")}</Link>
          <Link to={book.to} onClick={close} className={`${book.cls} !h-11`} data-testid="mobile-book-btn">{book.label}</Link>
        </div>
      </SheetContent>
    </Sheet>
  );
}
